import { FormEvent, useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import { sendEmailVerification } from "firebase/auth";
import { ExternalLink, Lightbulb, LogOut, ThumbsUp, X } from "lucide-react";
import { seedRoadmap, permanentNonGoals, qualityCommitments } from "../data/roadmap";
import { communityApi } from "../lib/community";
import type { CommunityProfile, RoadmapItem, RoadmapResponse, SuggestionInput } from "../types";

const statusLabels = {
  under_consideration: "Under consideration",
  planned: "Planned",
  in_progress: "In progress",
  shipped: "Shipped",
  not_planned: "Not planned"
} as const;

function SuggestionDialog({ user, open, onClose }: { user: User; open: boolean; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [problem, setProblem] = useState("");
  const [useCase, setUseCase] = useState("");
  const [details, setDetails] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [suggestionId, setSuggestionId] = useState("");

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!acknowledged) return;
    setBusy(true);
    setError("");
    try {
      const input: SuggestionInput = { title, problem, useCase, details: details || undefined, safetyAcknowledgement: true };
      const result = await communityApi.suggest(user, input);
      setSuggestionId(result.suggestionId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The idea could not be published.");
    } finally { setBusy(false); }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="dialog suggestion-dialog" role="dialog" aria-modal="true" aria-labelledby="suggest-title">
        <button className="icon-button dialog-close" onClick={onClose} aria-label="Close suggestion dialog"><X /></button>
        <Lightbulb className="dialog-mark" aria-hidden="true" />
        <span className="eyebrow">Mashr moderation queue</span>
        <h2 id="suggest-title">Suggest an improvement</h2>
        {suggestionId ? (
          <div className="published-panel" role="status">
            <strong>Your suggestion was received.</strong>
            <p>Reference: <code>{suggestionId}</code>. It remains private during moderation and is not a roadmap commitment unless promoted to an Epic or Story.</p>
          </div>
        ) : (
          <form className="suggestion-form" onSubmit={submit}>
            <label>Short title<input required minLength={10} maxLength={120} value={title} onChange={(event) => setTitle(event.target.value)} /></label>
            <label>What problem should this solve?<textarea required minLength={50} maxLength={2000} value={problem} onChange={(event) => setProblem(event.target.value)} /></label>
            <label>Who needs it, and when?<textarea required minLength={30} maxLength={1200} value={useCase} onChange={(event) => setUseCase(event.target.value)} /></label>
            <label>Optional detail<textarea maxLength={4000} value={details} onChange={(event) => setDetails(event.target.value)} /></label>
            <label className="checkbox-label">
              <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
              <span>I have not included case evidence, credentials, personal information, or sensitive incident details.</span>
            </label>
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            <button className="button primary" disabled={busy || !acknowledged}>{busy ? "Submitting…" : "Submit suggestion"}</button>
          </form>
        )}
      </section>
    </div>
  );
}

function VoteButton({ item, disabled, onVote }: { item: RoadmapItem; disabled: boolean; onVote: (item: RoadmapItem) => void }) {
  return (
    <button
      className={`vote-button ${item.currentUserVoted ? "voted" : ""}`}
      aria-pressed={item.currentUserVoted}
      disabled={disabled}
      onClick={() => onVote(item)}
      title="Votes count once per verified Mashr account"
    >
      <ThumbsUp size={16} />
      <span>{item.verifiedVoteCount}</span>
      <span className="vote-label">registered {item.verifiedVoteCount === 1 ? "vote" : "votes"}</span>
    </button>
  );
}

export function Roadmap({ user, onAuthRequest, onSignOut }: { user: User | null; onAuthRequest: () => void; onSignOut: () => void }) {
  const [roadmap, setRoadmap] = useState<RoadmapResponse>(seedRoadmap);
  const [profile, setProfile] = useState<CommunityProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyIssue, setBusyIssue] = useState<number | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    let current = true;
    setLoading(true);
    Promise.all([
      communityApi.roadmap(user).catch(() => seedRoadmap),
      user ? communityApi.me(user).catch(() => ({ authenticated: true, emailVerified: user.emailVerified })) : Promise.resolve(null)
    ]).then(([nextRoadmap, nextProfile]) => {
      if (!current) return;
      setRoadmap(nextRoadmap);
      setProfile(nextProfile);
      setLoading(false);
    });
    return () => { current = false; };
  }, [user]);

  const epics = useMemo(() => roadmap.items.filter((item) => item.type === "epic"), [roadmap]);
  const storiesByParent = useMemo(() => {
    const grouped = new Map<number, RoadmapItem[]>();
    for (const item of roadmap.items) {
      if (item.type !== "user_story" || item.parentIssueNumber === undefined) continue;
      grouped.set(item.parentIssueNumber, [...(grouped.get(item.parentIssueNumber) || []), item]);
    }
    return grouped;
  }, [roadmap]);

  async function ensureParticipant(): Promise<boolean> {
    setError("");
    setNotice("");
    if (!user) { onAuthRequest(); return false; }
    if (!user.emailVerified && !profile?.emailVerified) {
      setError("Verify your Mashr account email before voting or suggesting.");
      return false;
    }
    return true;
  }

  async function resendVerification() {
    if (!user) return;
    setError("");
    setNotice("");
    try {
      await sendEmailVerification(user);
      setNotice("Verification email sent. Return here after using the link, then refresh verification.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The verification email could not be sent."); }
  }

  async function refreshVerification() {
    if (!user) return;
    setError("");
    setNotice("");
    try {
      await user.reload();
      await user.getIdToken(true);
      const nextProfile = await communityApi.me(user);
      setProfile(nextProfile);
      setNotice(nextProfile.emailVerified ? "Email verification confirmed." : "The account is not verified yet.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Verification status could not be refreshed."); }
  }

  async function vote(item: RoadmapItem) {
    if (!(await ensureParticipant())) return;
    if (!user) return;
    setBusyIssue(item.issueNumber);
    try {
      const next = !item.currentUserVoted;
      const result = await communityApi.vote(user, item.issueNumber, next);
      setRoadmap((value) => ({
        ...value,
        items: value.items.map((candidate) => candidate.issueNumber === item.issueNumber
          ? { ...candidate, currentUserVoted: result.voted, verifiedVoteCount: result.verifiedVoteCount }
          : candidate)
      }));
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The vote could not be updated."); }
    finally { setBusyIssue(null); }
  }

  async function openSuggestion() {
    if (await ensureParticipant()) setSuggesting(true);
  }

  return (
    <section id="roadmap" className="section roadmap-section" aria-labelledby="roadmap-title">
      <div className="section-heading roadmap-heading">
        <div>
          <span className="eyebrow">Community priorities, not promises</span>
          <h2 id="roadmap-title">Help shape what Blockr investigates next.</h2>
          <p>Browse without an account. Verified Mashr users can vote independently on high-level Epics and their Story details.</p>
        </div>
        <div className="roadmap-actions">
          {!user ? <button className="button secondary" onClick={onAuthRequest}>Sign in to participate</button> : null}
          <button id="suggest-an-idea" className="button primary" onClick={openSuggestion}><Lightbulb size={17} /> Suggest an idea</button>
        </div>
      </div>

      {loading ? <p className="subtle-status" role="status">Refreshing the public roadmap…</p> : null}
      {roadmap.source === "seed" ? <p className="subtle-status">Preview data: live Mashr registered-user votes will replace these zero-count seed items when the shared service is available.</p> : null}
      {notice ? <p className="subtle-status" role="status">{notice}</p> : null}
      {error ? <p className="form-error roadmap-error" role="alert">{error}</p> : null}

      <div className="roadmap-grid">
        {epics.map((epic) => {
          const stories = storiesByParent.get(epic.issueNumber) || [];
          return (
            <article id={`item-${epic.issueNumber}`} className="epic-card" key={epic.issueNumber}>
              <div className="epic-topline">
                <span className="card-badge">Epic · {epic.category}</span>
                <span className={`status status-${epic.status}`}>{statusLabels[epic.status]}</span>
              </div>
              <h3>{epic.title}</h3>
              <p>{epic.summary}</p>
              {epic.iterationStart ? <p className="iteration">Target week: {new Date(epic.iterationStart).toLocaleDateString()}</p> : null}
              <div className="epic-controls">
                <VoteButton item={epic} disabled={busyIssue === epic.issueNumber} onVote={vote} />
                <a className="text-link" href={epic.issueUrl}>Permalink <ExternalLink size={13} /></a>
              </div>
              {stories.length ? (
                <details className="story-list">
                  <summary>{stories.length} proposed {stories.length === 1 ? "story" : "stories"}</summary>
                  <ul>
                    {stories.map((story) => (
                      <li id={`item-${story.issueNumber}`} key={story.issueNumber}>
                        <div><strong>{story.title}</strong><span>{statusLabels[story.status]}</span></div>
                        <VoteButton item={story} disabled={busyIssue === story.issueNumber} onVote={vote} />
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </article>
          );
        })}
      </div>

      <div className="roadmap-policy-grid">
        <article>
          <h3>Engineering quality is not a popularity contest.</h3>
          <ul>{qualityCommitments.map((item) => <li key={item}>{item}</li>)}</ul>
        </article>
        <article>
          <h3>Explicit non-goals</h3>
          <ul>{permanentNonGoals.map((item) => <li key={item}>{item}</li>)}</ul>
        </article>
      </div>

      <p className="roadmap-policy">Votes are advisory. Safety, correctness, accessibility, licensing, and release validation can override popularity. Scheduled work uses one-week targets; a release ships only after its gates pass.</p>

      {user ? (
        <div className="account-strip">
          <span>Signed in as {user.email || "a Mashr user"}</span>
          {profile?.emailVerified === false ? <button onClick={resendVerification}>Resend verification</button> : null}
          {profile?.emailVerified === false ? <button onClick={refreshVerification}>Refresh verification</button> : null}
          <button onClick={onSignOut}><LogOut size={14} /> Sign out</button>
        </div>
      ) : null}
      {user ? <SuggestionDialog user={user} open={suggesting} onClose={() => setSuggesting(false)} /> : null}
    </section>
  );
}
